<template>
  <!-- Nothing at all when the visitor's country charges no VAT. "incl.
       0 % VAT" reads like a claim about the shop rather than about the
       jurisdiction, and for a US visitor it would simply be wrong. -->
  <span v-if="show" class="vat-note" :class="variant">
    {{ $t('shop.vat_included', { rate: ctx.formattedRate }) }}

    <template v-if="pickable">
      <button
        type="button"
        class="vat-country"
        :title="$t('shop.vat_change_country', 'Change country')"
        @click.stop.prevent="open = !open"
      >{{ ctx.country }}</button>

      <span v-if="open" class="vat-pop" @click.stop>
        <span class="vat-pop-title">{{ $t('shop.vat_country_title', 'Which country are you buying from?') }}</span>
        <select class="vat-select" :value="ctx.country" @change="choose">
          <option v-for="c in ctx.countries" :key="c" :value="c">{{ c }}</option>
        </select>
        <span class="vat-pop-hint">{{ $t('shop.vat_country_hint', '') }}</span>
      </span>
    </template>
  </span>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
/**
 * The "incl. 19 % VAT" line under a price.
 *
 * Required beneath consumer prices in Germany (PAngV): the price shown is
 * gross, and that has to be stated. The rate itself is the visitor's
 * jurisdiction — see server/utils/vatContext.ts for how it is resolved.
 *
 * The picker is deliberately opt-in per usage. On a grid of forty cards
 * forty popovers would be absurd, so cards render the bare note and only
 * detail pages and the cart let the visitor correct the country.
 */
const props = defineProps({
  /** 'card' is the compact form for grids; 'detail' is roomier. */
  variant: { type: String, default: 'card' },
  /** Show the country code as a button that opens the picker. */
  pickable: { type: Boolean, default: false }
})

const { ctx, setCountry } = useVatNote()
const open = ref(false)

// Resolved during SSR by the composable, so the note is in the initial
// HTML rather than appearing after hydration — see useVatNote for why
// that matters for a required price disclosure.
const show = computed(() => !!ctx.value?.applicable && !!ctx.value?.formattedRate)

async function choose(e) {
  open.value = false
  await setCountry(e.target.value)
}

// Close on any outside click. Registered only while open so the shop does
// not carry a document listener per rendered price.
function onDocClick() { open.value = false }
watch(open, (isOpen) => {
  if (import.meta.server) return
  if (isOpen) document.addEventListener('click', onDocClick)
  else document.removeEventListener('click', onDocClick)
})
onBeforeUnmount(() => {
  if (import.meta.client) document.removeEventListener('click', onDocClick)
})
</script>

<style scoped>
.vat-note {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.3;
}
.vat-note.card { font-size: 0.65rem; }
.vat-note.detail { font-size: 0.75rem; }

.vat-country {
  font-variant-numeric: tabular-nums;
  border-bottom: 1px dotted rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.55);
  transition: color 0.15s;
}
.vat-country:hover {
  color: rgba(255, 255, 255, 0.85);
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.vat-pop {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 240px;
  padding: 0.7rem 0.8rem;
  background: var(--cy-module, var(--surface-color));
  border: 1px solid var(--cy-rim, var(--border));
  border-radius: 3px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
  white-space: normal;
  text-align: left;
}
.vat-pop-title { color: var(--text-main); font-size: 0.72rem; font-weight: 600; }
.vat-pop-hint { color: rgba(255, 255, 255, 0.35); font-size: 0.62rem; line-height: 1.4; }

.vat-select {
  padding: 0.35rem 0.5rem;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--cy-rim, var(--border));
  color: var(--text-main);
  font-size: 0.75rem;
}
</style>
